import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Search, Type, MessageCircleQuestion, Download } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { ImageField } from "./ImageField";
import { useAdminT } from "./adminI18n";
import { STRINGS } from "../../i18n";

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

const TEXT_KEYS = [
  { key: "courses_eyebrow", label: "الدورات — النبذة" },
  { key: "courses_title_a", label: "الدورات — العنوان (الجزء 1)" },
  { key: "courses_title_b", label: "الدورات — العنوان (الجزء 2 بالذهبي)" },
  { key: "courses_desc", label: "الدورات — الوصف" },
  { key: "manifesto_eyebrow", label: "المنهجية — النبذة" },
  { key: "manifesto_title_a", label: "المنهجية — العنوان (الجزء 1)" },
  { key: "manifesto_title_b", label: "المنهجية — العنوان (الجزء 2 بالذهبي)" },
  { key: "testi_eyebrow", label: "آراء العملاء — النبذة" },
  { key: "testi_title_a", label: "آراء العملاء — العنوان (الجزء 1)" },
  { key: "testi_title_b", label: "آراء العملاء — العنوان (الجزء 2 بالذهبي)" },
  { key: "clients_eyebrow", label: "العملاء — النبذة" },
  { key: "clients_title_a", label: "العملاء — العنوان (الجزء 1)" },
  { key: "clients_title_b", label: "العملاء — العنوان (الجزء 2 بالذهبي)" },
  { key: "clients_cta", label: "العملاء — نص الزر" },
  { key: "blog_eyebrow", label: "المقالات — النبذة" },
  { key: "blog_title_a", label: "المقالات — العنوان (الجزء 1)" },
  { key: "blog_title_b", label: "المقالات — العنوان (الجزء 2 بالذهبي)" },
  { key: "faq_eyebrow", label: "الأسئلة الشائعة — النبذة" },
  { key: "faq_title_a", label: "الأسئلة الشائعة — العنوان (الجزء 1)" },
  { key: "faq_title_b", label: "الأسئلة الشائعة — العنوان (الجزء 2 بالذهبي)" },
  { key: "contact_eyebrow", label: "التواصل — النبذة" },
  { key: "contact_title_a", label: "التواصل — العنوان (الجزء 1)" },
  { key: "contact_title_b", label: "التواصل — العنوان (الجزء 2 بالذهبي)" },
  { key: "contact_text", label: "التواصل — النص" },
  { key: "footer_text", label: "نص التذييل" },
  { key: "banners_title_a", label: "الفعاليات القادمة — العنوان (الجزء 1)" },
  { key: "banners_title_b", label: "الفعاليات القادمة — العنوان (الجزء 2)" },
  { key: "consult_eyebrow", label: "الاستشارات — النبذة" },
];

export const TextsTab = () => {
  const { t } = useAdminT();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`).then((res) => {
      const d = res.data;
      setForm({
        seo_title: d.seo_title || "",
        seo_title_en: d.seo_title_en || "",
        seo_description: d.seo_description || "",
        seo_description_en: d.seo_description_en || "",
        og_image: d.og_image || "",
        hero_line1: d.hero_line1 || "",
        hero_line2: d.hero_line2 || "",
        hero_line3: d.hero_line3 || "",
        hero_subtext: d.hero_subtext || "",
        dual_eyebrow: d.dual_eyebrow || "",
        dual_title_a: d.dual_title_a || "",
        dual_title_b: d.dual_title_b || "",
        dual_individuals_image: d.dual_individuals_image || "",
        dual_organizations_image: d.dual_organizations_image || "",
        texts: { ar: { ...((d.texts || {}).ar || {}) }, en: { ...((d.texts || {}).en || {}) } },
        faqs: d.faqs || [],
      });
    }).catch(() => toast.error(t("تعذر تحميل الإعدادات")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!form) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>;
  }

  const setText = (lang, key) => (e) =>
    setForm({ ...form, texts: { ...form.texts, [lang]: { ...form.texts[lang], [key]: e.target.value } } });

  const setFaq = (i, k) => (e) => {
    const faqs = [...form.faqs];
    faqs[i] = { ...faqs[i], [k]: e.target.value };
    setForm({ ...form, faqs });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const clean = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => (v || "").trim() !== ""));
      await axios.put(`${API}/admin/settings`, {
        seo_title: form.seo_title,
        seo_title_en: form.seo_title_en,
        seo_description: form.seo_description,
        seo_description_en: form.seo_description_en,
        og_image: form.og_image,
        hero_line1: form.hero_line1,
        hero_line2: form.hero_line2,
        hero_line3: form.hero_line3,
        hero_subtext: form.hero_subtext,
        dual_eyebrow: form.dual_eyebrow,
        dual_title_a: form.dual_title_a,
        dual_title_b: form.dual_title_b,
        dual_individuals_image: form.dual_individuals_image,
        dual_organizations_image: form.dual_organizations_image,
        texts: { ar: clean(form.texts.ar), en: clean(form.texts.en) },
        faqs: form.faqs,
      }, { headers: authHeaders() });
      toast.success(t("تم حفظ النصوص — حدّثي الصفحة الرئيسية لرؤية التغييرات"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const L = ({ children }) => <label className="block text-sm text-luxury-gray mb-2">{children}</label>;

  const HERO_FIELDS = [
    { key: "hero_line1", label: "الهيرو — العنوان (سطر 1)" },
    { key: "hero_line2", label: "الهيرو — العنوان (سطر 2)" },
    { key: "hero_line3", label: "الهيرو — العنوان (سطر 3 بالذهبي)" },
    { key: "hero_subtext", label: "الهيرو — النص الداعم" },
    { key: "dual_eyebrow", label: "لمن نعمل — النبذة" },
    { key: "dual_title_a", label: "لمن نعمل — العنوان (الجزء 1)" },
    { key: "dual_title_b", label: "لمن نعمل — العنوان (الجزء 2 بالذهبي)" },
  ];

  return (
    <form onSubmit={save} className="space-y-10" data-testid="admin-texts-tab">
      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-5" data-testid="homepage-content-section">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2 flex items-center gap-3">
          <Type className="w-6 h-6 text-luxury-gold" strokeWidth={1.5} /> {t("محتوى الصفحة الرئيسية")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {HERO_FIELDS.map((f) => (
            <div key={f.key} className={f.key === "hero_subtext" ? "md:col-span-2" : ""}>
              <L>{t(f.label)}</L>
              <input data-testid={`texts-${f.key}`} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className={inputCls} />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ImageField testid="dual-individuals-image" label={t("صورة بطاقة «للأفراد»")} value={form.dual_individuals_image} onChange={(v) => setForm({ ...form, dual_individuals_image: v })} />
          <ImageField testid="dual-organizations-image" label={t("صورة بطاقة «للمؤسسات»")} value={form.dual_organizations_image} onChange={(v) => setForm({ ...form, dual_organizations_image: v })} />
        </div>
        <p className="text-luxury-gray/70 text-xs">{t("اتركي خانة الصورة فارغة لاستخدام الصورة الافتراضية.")}</p>
      </section>
      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-5">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2 flex items-center gap-3">
          <Search className="w-6 h-6 text-luxury-gold" strokeWidth={1.5} /> {t("الظهور على Google (SEO)")}
        </h2>        <p className="text-luxury-gray text-sm">{t("هذه المعلومات تظهر في نتائج البحث وعند مشاركة رابط الموقع على واتساب ووسائل التواصل.")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><L>{t("عنوان الموقع (عربي)")}</L><input data-testid="seo-title-ar" value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className={inputCls} /></div>
          <div><L>{t("عنوان الموقع (إنجليزي)")}</L><input data-testid="seo-title-en" dir="ltr" value={form.seo_title_en} onChange={(e) => setForm({ ...form, seo_title_en: e.target.value })} className={inputCls} /></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><L>{t("وصف الموقع (عربي)")}</L><textarea data-testid="seo-desc-ar" rows={3} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className={inputCls} /></div>
          <div><L>{t("وصف الموقع (إنجليزي)")}</L><textarea data-testid="seo-desc-en" dir="ltr" rows={3} value={form.seo_description_en} onChange={(e) => setForm({ ...form, seo_description_en: e.target.value })} className={inputCls} /></div>
        </div>
        <ImageField testid="seo-og-image" label={t("صورة المشاركة (تظهر عند نشر الرابط)")} value={form.og_image} onChange={(v) => setForm({ ...form, og_image: v })} />
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-5">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2 flex items-center gap-3">
          <Type className="w-6 h-6 text-luxury-gold" strokeWidth={1.5} /> {t("نصوص أقسام الصفحة الرئيسية")}
        </h2>
        <p className="text-luxury-gray text-sm">{t("عدّلي أي عنوان أو نص — الحقل الفارغ يعني استخدام النص الحالي الافتراضي.")}</p>
        {TEXT_KEYS.map((tk) => (
          <div key={tk.key} data-testid={`text-row-${tk.key}`} className="border border-luxury-border p-4 space-y-3">
            <p className="text-luxury-gold text-sm">{t(tk.label)}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input data-testid={`text-ar-${tk.key}`} value={form.texts.ar[tk.key] || ""} onChange={setText("ar", tk.key)} placeholder={STRINGS.ar[tk.key] || ""} className={inputCls} />
              <input data-testid={`text-en-${tk.key}`} dir="ltr" value={form.texts.en[tk.key] || ""} onChange={setText("en", tk.key)} placeholder={STRINGS.en[tk.key] || ""} className={inputCls} />
            </div>
          </div>
        ))}
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-5">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2 flex items-center gap-3">
          <MessageCircleQuestion className="w-6 h-6 text-luxury-gold" strokeWidth={1.5} /> {t("محتوى الأسئلة الشائعة")}
        </h2>
        <p className="text-luxury-gray text-sm">{t("أضيفي أو عدّلي الأسئلة والأجوبة. إذا كانت القائمة فارغة يعرض الموقع الأسئلة الافتراضية.")}</p>
        {form.faqs.length === 0 && (
          <button type="button" data-testid="faq-import-defaults" onClick={() => setForm({
            ...form,
            faqs: STRINGS.ar.faqs.map((f, i) => ({ q: f.q, a: f.a, q_en: STRINGS.en.faqs[i]?.q || "", a_en: STRINGS.en.faqs[i]?.a || "" })),
          })} className="flex items-center gap-2 border border-luxury-gold text-luxury-gold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-gold hover:text-luxury-black transition-colors">
            <Download className="w-4 h-4" strokeWidth={1.5} /> {t("استيراد الأسئلة الحالية للتعديل عليها")}
          </button>
        )}
        {form.faqs.map((f, i) => (
          <div key={i} data-testid={`faq-row-${i}`} className="border border-luxury-border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-luxury-gold text-sm">{t("سؤال")} {i + 1}</p>
              <button type="button" data-testid={`faq-delete-${i}`} onClick={() => setForm({ ...form, faqs: form.faqs.filter((_, x) => x !== i) })} className="text-red-400 hover:text-red-300 p-1">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input data-testid={`faq-q-${i}`} value={f.q} onChange={setFaq(i, "q")} placeholder={t("السؤال (عربي)")} className={inputCls} />
              <input data-testid={`faq-q-en-${i}`} dir="ltr" value={f.q_en || ""} onChange={setFaq(i, "q_en")} placeholder="Question (EN)" className={inputCls} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <textarea data-testid={`faq-a-${i}`} rows={2} value={f.a} onChange={setFaq(i, "a")} placeholder={t("الجواب (عربي)")} className={inputCls} />
              <textarea data-testid={`faq-a-en-${i}`} dir="ltr" rows={2} value={f.a_en || ""} onChange={setFaq(i, "a_en")} placeholder="Answer (EN)" className={inputCls} />
            </div>
          </div>
        ))}
        {form.faqs.length > 0 && (
          <button type="button" data-testid="faq-add" onClick={() => setForm({ ...form, faqs: [...form.faqs, { q: "", a: "", q_en: "", a_en: "" }] })} className="flex items-center gap-2 border border-luxury-border text-luxury-bone px-5 py-2.5 rounded-full text-sm hover:border-luxury-gold transition-colors">
            <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("أضيفي سؤالاً جديداً")}
          </button>
        )}
      </section>

      <button data-testid="texts-save-button" type="submit" disabled={saving} className="flex items-center gap-3 bg-luxury-gold text-luxury-black font-semibold px-10 py-4 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" strokeWidth={1.5} />}
        {t("حفظ كل النصوص")}
      </button>
    </form>
  );
};
